/**
 * Plans, limits and the free trial.
 *
 * Modelled on OnPay.my's packages: a free account that is valid for 90 days to
 * convert to a paid plan, then Basic, Lite and Premium with growing limits. The
 * numbers here are only the DEFAULTS written when the plans are first created —
 * after that the super admin edits prices and limits in the database, and
 * nothing reads these constants for a live decision except `FREE_PLAN_CODE` and
 * `TRIAL_DAYS`.
 */
/** How long a free account is valid before it must convert to a paid plan. */
export const TRIAL_DAYS = 90;
export const FREE_PLAN_CODE = 'free';
/** Things a plan can cap. A limit of `null` means unlimited. */
export const PLAN_LIMIT_KEYS = ['products', 'salesPages'];
export const PLAN_LIMIT_LABELS = {
    products: 'Products',
    salesPages: 'Sales pages',
};
/** Things a plan can switch on. */
export const PLAN_FEATURES = ['online_gateways'];
export const PLAN_FEATURE_LABELS = {
    online_gateways: 'Online payment gateways (SenangPay, ToyyibPay, iPay88, Fiuu, PayPal, Stripe and more)',
};
/**
 * Yearly prices are the TOTAL for twelve months (OnPay's lower per-month price
 * for a longer commitment). Their "forms" correspond to our sales pages.
 */
export const DEFAULT_PLANS = [
    {
        code: FREE_PLAN_CODE,
        name: 'Free',
        description: `Free for ${TRIAL_DAYS} days. Take payments by bank-in or QR.`,
        priceMonthly: '0.00',
        priceYearly: null,
        limits: { products: 3, salesPages: 1 },
        features: [],
        position: 0,
    },
    {
        code: 'basic',
        name: 'Basic',
        description: 'For a small shop getting started online.',
        priceMonthly: '72.00',
        priceYearly: '396.00',
        limits: { products: 15, salesPages: 3 },
        features: ['online_gateways'],
        position: 1,
    },
    {
        code: 'lite',
        name: 'Lite',
        description: 'For a growing catalogue and regular campaigns.',
        priceMonthly: '117.00',
        priceYearly: '936.00',
        limits: { products: 250, salesPages: 50 },
        features: ['online_gateways'],
        position: 2,
    },
    {
        code: 'premium',
        name: 'Premium',
        description: 'For a large catalogue and many campaigns.',
        priceMonthly: '182.00',
        priceYearly: '1776.00',
        limits: { products: 5000, salesPages: 500 },
        features: ['online_gateways'],
        position: 3,
    },
];
const DAY_MS = 86_400_000;
/**
 * Where a merchant stands. Decided from the dates alone, so it can never drift
 * from the period it describes: once the period ends the account is EXPIRED,
 * whatever the stored status still says.
 */
export function subscriptionState(input) {
    if (input.currentPeriodEnd.getTime() <= input.now.getTime())
        return 'EXPIRED';
    return input.planCode === FREE_PLAN_CODE ? 'TRIAL' : 'ACTIVE';
}
/** Whole days left, rounded up, never below zero. */
export function daysLeft(end, now) {
    return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / DAY_MS));
}
/** True when using one more would go over the cap. `null`/missing = unlimited. */
export function limitReached(limit, used) {
    return typeof limit === 'number' && used >= limit;
}
export function trialEndsAt(from) {
    return new Date(from.getTime() + TRIAL_DAYS * DAY_MS);
}
/** Add calendar months in UTC, clamping to the last day (31 Jan + 1 month = 28/29 Feb). */
export function addMonths(date, months) {
    const result = new Date(date.getTime());
    const day = result.getUTCDate();
    result.setUTCDate(1);
    result.setUTCMonth(result.getUTCMonth() + months);
    const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate();
    result.setUTCDate(Math.min(day, lastDay));
    return result;
}
/**
 * The paid period a successful payment buys.
 *
 * Renewing the plan you are already paid up on stacks the new period after the
 * current one, so paying early never loses days. Anything else — first purchase,
 * moving to a different plan, paying after it lapsed — starts now. Days left on a
 * free trial or on a different plan are not carried over (no proration).
 */
export function nextPaidPeriod(input) {
    const renewing = input.current !== null &&
        input.current.isPaid &&
        input.current.planId === input.newPlanId &&
        input.current.endsAt.getTime() > input.now.getTime();
    const start = renewing && input.current ? input.current.endsAt : input.now;
    return { start, end: addMonths(start, input.interval === 'YEARLY' ? 12 : 1) };
}
//# sourceMappingURL=plans.js.map